// The HR hold's one question, as a card on the list (BUGS.md M46; module 05 R70; migration 241). Cobrain keeps a call
// to the people on it, and mails it to nobody, when any part of it is read as pay, a named person's job, performance,
// discipline or exit, or staff cuts; its organiser is asked once: keep it, or release it to be read and mailed as any
// call. Mike by pop-up, 7 Oct 2026 about 13:30 CEST, "One list per organiser (Recommended)": "One card per organiser:
// "35 older calls are held", opening to the list with Keep or Release on each. New calls from now get one question
// each." One call held reads as its own question; more read as the list. Unanswered, a call stays held: nothing is
// inferred from silence, and folding the card answers nothing.

import { CardFrame, Chip } from './parts/primitives.js';
import { ActionButton } from './parts/ActionButton.js';
import { viewFetch as fetch } from './view-as.js';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, Unauthenticated } from './api.js';
import { count, formatDate } from './labels.js';

export interface HeldCall { signalId: string; meetingId: string | null; title: string | null; startedAt: string | null; heldAt: string }

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const heldCalls = (): Promise<{ calls: HeldCall[] }> =>
  fetch('/api/meeting-holds', { credentials: 'same-origin' }).then((r) => json<{ calls: HeldCall[] }>(r));
export const answerHeld = (signalId: string, answer: 'keep' | 'release'): Promise<{ calls: HeldCall[] }> =>
  fetch(`/api/meeting-holds/${encodeURIComponent(signalId)}`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ answer }),
  }).then((r) => json<{ calls: HeldCall[] }>(r));

function HeldRow({ call, busy, onAnswer, go }: { call: HeldCall; busy: boolean; onAnswer: (a: 'keep' | 'release') => void; go: (path: string) => void }) {
  const title = call.title ?? 'A call';
  return (
    <li className="held-call">
      <p className="plain">
        {call.meetingId ? <ActionButton reading type="button" className="link inline" onClick={() => go(`/meetings/${call.meetingId}`)}>{title}</ActionButton> : title}
        {call.startedAt ? <span className="note"> · {formatDate(call.startedAt)}</span> : null}
      </p>
      <div className="choices">
        <ActionButton className="choice" disabled={busy} onClick={() => onAnswer('keep')}>
          <strong>Keep</strong>
          <span className="gist">Only the people on the call read it, and they are mailed its summary.</span>
        </ActionButton>
        <ActionButton className="choice" disabled={busy} onClick={() => onAnswer('release')}>
          <strong>Release</strong>
          <span className="gist">It is read and mailed as any call.</span>
        </ActionButton>
      </div>
    </li>
  );
}

export function HeldCallsCard({ go }: { go: (path: string) => void }) {
  const [calls, setCalls] = useState<HeldCall[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const read = useCallback(() => {
    let live = true;
    heldCalls().then((r) => { if (live) setCalls(r.calls); }).catch(() => { if (live) setCalls([]); });
    return () => { live = false; };
  }, []);
  useEffect(read, [read]);
  if (calls.length === 0) return null;

  const answer = async (signalId: string, a: 'keep' | 'release'): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setCalls((await answerHeld(signalId, a)).calls);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nothing was changed: the answer could not be sent.');
    } finally {
      setBusy(false);
    }
  };
  const one = calls.length === 1 ? calls[0]! : null;
  const head = one ? `Keep or release: ${one.title ?? 'a call held for your answer'}?` : `${count(calls.length, 'call')} held for your answer`;

  return (
    <CardFrame className="questions held-calls" data-item="held-calls" aria-labelledby="held-title">
      <div className="meta">
        <Chip className="brain">Cobrain is holding</Chip>
        <span className="when"><ActionButton reading className="link inline" onClick={() => setOpen((v) => !v)}>{open ? 'not now' : 'answer'}</ActionButton></span>
      </div>
      <p className="head" id="held-title">{head}</p>
      {open ? (
        <div aria-live="polite">
          <p className="note">
            Part of {one ? 'this call' : 'each'} is about pay or a person's job, so Cobrain keeps it to the people on it and
            mails it to nobody until you answer.
          </p>
          <ul className="held-list">
            {calls.map((c) => <HeldRow key={c.signalId} call={c} busy={busy} onAnswer={(a) => void answer(c.signalId, a)} go={go} />)}
          </ul>
          {error ? <p className="note" role="alert">{error}</p> : null}
        </div>
      ) : null}
    </CardFrame>
  );
}
